'use client';
import { type Brand } from '@curology/ui-components-web/brand';
import { Progress } from '@curology/ui-components-web/react/Progress';
import React, { useEffect, useRef } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { type ExpressCurologySignUpSkinPdpSourceConfigType } from '@/components/flow/expressCurologySignUpSkin/types';
import { useBraintree } from '@/components/payments/hooks/useBraintree';
import { usePayPalClient } from '@/components/payments/hooks/usePayPalClient';
import { PAYPAL_CONSTANTS } from '@/consts/payments';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { getPaymentErrorMessage } from '@/helpers/productSelectionAndCheckout/getPaymentErrorMessage';
import { reportUnhandledPaymentErrorToSentry } from '@/helpers/productSelectionAndCheckout/reportUnhandledPaymentErrorToSentry';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import { updateRegistrationEligibility } from '@/services/eligibilityService/actions';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import { PayPalService } from '@/services/productSelectionAndCheckoutService/payPal';
import { editSessionBag } from '@/services/visitsService/client';
import { AnalyticsEvent, FlowTypes, trackEvent } from '@/utils/analytics';

import { useNavigation } from '../../../hooks/useNavigation';

// This component is primarily based off the code in apps/curology-sign-up/src/components/payments/components/paypalButton.tsx.
export const PayPalButton = ({
  brand,
  bundleConfig,
  disableForms,
  enableForms,
  isSubmitting,
  payPalBlockForms,
  payPalUnblockForms,
  selectedAddOnProductItemIds,
  treatmentCategory,
  uuid,
}: {
  brand: Brand;
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType;
  disableForms: () => void;
  enableForms: () => void;
  isSubmitting: boolean;
  payPalBlockForms: () => void;
  payPalUnblockForms: () => void;
  selectedAddOnProductItemIds: number[];
  treatmentCategory: TreatmentCategory;
  uuid: string;
}) => {
  const braintree = useBraintree();
  const payPalClient = usePayPalClient();
  const hasRenderedRef = useRef(false);

  const flashMessages = useFlashMessages();
  const { handleIfSessionExpired } = useSessionExpired(brand);
  const { onNext } = useNavigation();

  const payPalButtonContainerId = 'paypal-button-container';
  const payPalButtonContainerIdSelector = `#${payPalButtonContainerId}`;

  // Reset rendering state on unmount so the button can re-render on re-mount
  useEffect(() => {
    return () => {
      hasRenderedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!braintree.clients || !payPalClient?.Button) {
      return;
    }

    // Only render the PayPal button once per mount cycle to prevent
    // "Page already has an active payment session" errors
    if (hasRenderedRef.current) {
      return;
    }
    hasRenderedRef.current = true;

    const {
      clients: {
        dataCollector: { deviceData },
        payPalCheckout,
      },
    } = braintree;

    const payPalButtonContainerElem = document.querySelector(
      payPalButtonContainerIdSelector,
    );

    if (payPalButtonContainerElem) {
      payPalButtonContainerElem.innerHTML = '';
    }

    trackEvent(AnalyticsEvent.PAYPAL_EXPRESS_CHECKOUT_LOADED, {
      flow: FlowTypes.PDP_CHECKOUT,
    });

    window['paypal'].Button.render(
      {
        env: braintree.env,
        onAuthorize: async (data) => {
          const authorizationResponse =
            await payPalCheckout.tokenizePayment(data);

          if (isSubmitting) {
            return authorizationResponse;
          }

          trackEvent(AnalyticsEvent.EXPRESS_CHECKOUT_CLICKED, {
            flow: FlowTypes.PDP_CHECKOUT,
            paymentMethod: 'Paypal',
          });

          try {
            disableForms();
            payPalBlockForms();

            const firstname = authorizationResponse.details.firstName.trim();
            const lastname = authorizationResponse.details.lastName.trim();

            if (firstname === '' || lastname === '') {
              flashMessages.error({
                title: 'Sorry!',
                message:
                  'We cannot proceed because your first name and last name are not set in PayPal.',
              });

              return authorizationResponse;
            }

            const selectedItemIds = [
              ...bundleConfig.setProducts
                .map((setProduct) => setProduct.itemIds)
                .flat(),
              ...selectedAddOnProductItemIds,
            ];

            await ProductSelectionAndCheckoutService.updateSelectedItems(
              brand,
              selectedItemIds,
              treatmentCategory,
            );

            const {
              details: { shippingAddress },
              nonce: paymentNonce,
            } = authorizationResponse;

            const {
              city = '',
              line1: address1 = '',
              line2: address2,
              postalCode: zipcode = '',
              state = '',
            } = shippingAddress ?? {};

            const method = 'paypal';
            const phoneNumberOptional = true;
            const vendor = 'braintree';

            const data = {
              address1,
              address2,
              city,
              deviceData,
              firstname,
              lastname,
              method,
              paymentNonce,
              phoneNumberOptional,
              state,
              vendor,
              zipcode,
            };

            await updateRegistrationEligibility(brand, {
              state,
              treatmentCategories: treatmentCategory,
            });

            await PayPalService.activateExpressCheckoutTrial({
              brand,
              data,
            });

            await editSessionBag({
              brand,
              sessionBag: {
                hasActivatedTrial: true,
                hasAddedPaymentMethod: true,
                hasAddedShippingAddress: true,
              },
              uuid,
            });

            flashMessages.success({
              title: 'Order confirmed!',
              message:
                'Now answer some quick questions to help your provider personalize your formula.',
            });

            trackEvent(AnalyticsEvent.PAYPAL_ACCOUNT_LINKED, {
              flow: FlowTypes.PDP_CHECKOUT,
            });

            onNext();
          } catch (e: any) {
            enableForms();
            payPalUnblockForms();

            if (handleIfSessionExpired(e.response?.status))
              return authorizationResponse;
            reportUnhandledPaymentErrorToSentry(e);
            const message = getPaymentErrorMessage(
              e.response?.data,
              'A system error occurred. Please try again.',
            );
            flashMessages.error({
              title: 'Sorry!',
              message,
            });
          }

          return authorizationResponse;
        },
        payment: () =>
          payPalCheckout.createPayment({
            displayName: brand,
            enableShippingAddress: true,
            flow: PAYPAL_CONSTANTS.VAULT,
            intent: PAYPAL_CONSTANTS.AUTHORIZE,
          }),
        style: {
          height: 55,
          label: PAYPAL_CONSTANTS.PAYPAL,
          shape: PAYPAL_CONSTANTS.PILL,
          size: PAYPAL_CONSTANTS.RESPONSIVE,
          tagline: false,
        },
      },
      payPalButtonContainerIdSelector,
    );
  }, [
    brand,
    bundleConfig,
    disableForms,
    enableForms,
    isSubmitting,
    payPalBlockForms,
    payPalUnblockForms,
    selectedAddOnProductItemIds,
    treatmentCategory,
    uuid,
    braintree,
    payPalClient,
    flashMessages,
    handleIfSessionExpired,
    onNext,
    payPalButtonContainerIdSelector,
  ]);

  return (
    <div className="cur-mt-4 cur-text-center">
      {!payPalClient?.Button ||
        (!braintree.clients && (
          <Progress
            className="cur-inline-block"
            progressType="circle"
            state="highlight"
          />
        ))}
      <div id={payPalButtonContainerId} />
    </div>
  );
};
